import type { ApolloClient } from "@apollo/client";
import { onError } from "@apollo/client/link/error";

/**
 * Global bridge for server-side plan limit enforcement.
 *
 * The server rejects requests that exceed a plan's lifetime limits with a
 * GraphQL error carrying `extensions.code === "PLAN_LIMIT_EXCEEDED"` (plus
 * optional `extensions.feature` and `extensions.limit`). The Apollo client is
 * created outside of React, so this module provides:
 *
 * 1. `planLimitLink` — an Apollo `onError` link that detects those errors.
 * 2. A tiny event emitter that the link publishes to.
 * 3. `installPlanLimitLink` — prepends the link onto an existing Apollo
 *    client's link chain (used by `PlanLimitWallListener`, which subscribes
 *    to the emitter and opens the ProPlanWall modal inside React).
 */

export type PlanLimitExceededEvent = {
  feature?: string;
  limit?: number | string;
};

type PlanLimitExceededHandler = (event: PlanLimitExceededEvent) => void;

const handlers = new Set<PlanLimitExceededHandler>();

/** Subscribe to plan-limit-exceeded events. Returns an unsubscribe fn. */
export function subscribeToPlanLimitExceeded(
  handler: PlanLimitExceededHandler
): () => void {
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
}

export function emitPlanLimitExceeded(event: PlanLimitExceededEvent): void {
  handlers.forEach((handler) => {
    try {
      handler(event);
    } catch (e) {
      console.error("Error in plan limit exceeded handler", e);
    }
  });
}

/**
 * Apollo error link that catches PLAN_LIMIT_EXCEEDED GraphQL errors from any
 * query/mutation in the app and broadcasts them to subscribers.
 */
export const planLimitLink = onError(({ graphQLErrors }) => {
  if (!graphQLErrors) return;

  for (const gqlError of graphQLErrors) {
    const extensions = gqlError.extensions;

    if (extensions && extensions.code === "PLAN_LIMIT_EXCEEDED") {
      emitPlanLimitExceeded({
        feature:
          typeof extensions.feature === "string"
            ? extensions.feature
            : undefined,
        limit:
          typeof extensions.limit === "number" ||
          typeof extensions.limit === "string"
            ? extensions.limit
            : undefined,
      });

      // One wall per operation is enough
      break;
    }
  }
});

/**
 * True when a caught Apollo error is a server-side plan-limit rejection.
 * Callers use this to skip generic error toasts — the ProPlanWall (opened by
 * the global bridge) is the user-facing response.
 */
export function isPlanLimitError(error: unknown): boolean {
  const gqlErrors = (error as any)?.graphQLErrors || [];
  return gqlErrors.some(
    (gqlError: any) => gqlError?.extensions?.code === "PLAN_LIMIT_EXCEEDED"
  );
}

let installed = false;

/**
 * Prepends `planLimitLink` to the given Apollo client's link chain. Safe to
 * call multiple times — only installs once. This lets us hook the existing
 * client (created in App.tsx) without modifying where it is constructed.
 */
export function installPlanLimitLink(client: ApolloClient<object>): void {
  if (installed) return;
  installed = true;

  client.setLink(planLimitLink.concat(client.link));
}
